import React, {useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, Shield, ArrowLeft, Check, Plus, Trash2, X } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import companyCollaboratorEditViewModel from '../../view-model/professional/company/company-collaborator-edit-view-model';

interface CompanyCollaboratorEditViewProps {
  onBack: () => void;
}

const CompanyCollaboratorEditView: React.FC<CompanyCollaboratorEditViewProps> = observer(({
  onBack,
}) => {
  const { t } = useTranslation();
  const vm = companyCollaboratorEditViewModel;

  // Charger les données au montage du composant
  useEffect(() => {
    vm.initialize().then();
  }, [vm]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-semibold">
            {t('collaborator.edit.title', 'Modifier les droits du collaborateur')}
          </h2>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">

        {/* En-tête avec photo et nom */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          {vm.data && (
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {vm.data && vm.data.collaborator.picture ? (
                      <img
                          src={vm.data.collaborator.picture}
                          alt={vm.getFormattedName(vm.data.collaborator.firstName, vm.data.collaborator.lastName)}
                          className="h-20 w-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                      />
                  ) : (
                      <div
                          className="h-20 w-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-2 border-gray-200 dark:border-gray-700">
                  <span className="text-xl font-semibold text-gray-500 dark:text-gray-400">
                    {vm.getInitials(vm.data.collaborator.firstName, vm.data.collaborator.lastName)}
                  </span>
                      </div>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {vm.getFormattedName(vm.data.collaborator.firstName, vm.data.collaborator.lastName)}
                  </h3>
                  <div className="mt-2">
                    {vm.selectedRoleId && (
                      <select
                          value={vm.selectedRoleId}
                          onChange={(e) => vm.setSelectedRoleId(e.target.value)}
                          className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-full text-sm font-medium text-gray-800 dark:text-gray-200 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {vm.availableRolesAndPermissions && vm.availableRolesAndPermissions.roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>
          )}

        </div>

        {/* Informations du collaborateur */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('collaborator.edit.contactInfo', 'Informations de contact')}
            </h4>

            <div className="space-y-3">
              {vm.data && vm.data.collaborator.firstName && vm.data.collaborator.lastName && (
                  <div className="flex items-center gap-3">
                    <User className="text-gray-400 flex-shrink-0" size={18}/>
                    <div className="text-gray-700 dark:text-gray-300">
                      {vm.getFormattedName(vm.data.collaborator.firstName, vm.data.collaborator.lastName)}
                    </div>
                  </div>
              )}


              {vm.data && vm.data.collaborator.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="text-gray-400 flex-shrink-0" size={18}/>
                    <div className="text-gray-700 dark:text-gray-300">{vm.data.collaborator.email}</div>
                  </div>
              )}


              {vm.data && vm.data.collaborator.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="text-gray-400 flex-shrink-0" size={18}/>
                    <div className="text-gray-700 dark:text-gray-300">{vm.data.collaborator.phone}</div>
                  </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('collaborator.edit.roleInfo', 'Rôle')}
            </h4>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
              <Shield className="text-gray-400" size={18} />
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('collaborator.edit.roleDescription', 'Sélectionnez un rôle pour ce collaborateur')}
                </h5>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {t('collaborator.edit.roleHint', 'Le rôle détermine le niveau d\'accès général du collaborateur.')}
              </p>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('collaborator.edit.permissions', 'Permissions')}
              {vm.availableRolesAndPermissions && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                ({vm.selectedPermissionIds.length}/{vm.availableRolesAndPermissions.permissions.length})
              </span>
              )}
            </h4>

            {/* Boutons d'action */}
            {!vm.permissionEditMode ? (
              <div className="flex gap-2">
                <button
                  onClick={() => vm.setPermissionEditMode('add')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Plus size={16} />
                  <span>{t('collaborator.edit.addPermissions', 'Accorder des droits')}</span>
                </button>
                <button
                  onClick={() => vm.setPermissionEditMode('remove')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  <Trash2 size={16} />
                  <span>{t('collaborator.edit.removePermissions', 'Supprimer des droits')}</span>
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => vm.handleCancelPermissionEdit()}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  <X size={16} />
                  <span>{t('collaborator.edit.cancel', 'Annuler')}</span>
                </button>
                <button
                  onClick={() => vm.permissionEditMode === 'add' ? vm.handleAddPermissions() : vm.handleRemovePermissions()}
                  disabled={vm.tempSelectedPermissionIds.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check size={16} />
                  <span>
                    {vm.permissionEditMode === 'add'
                      ? t('collaborator.edit.validate', 'Valider')
                      : t('collaborator.edit.validateRemove', 'Valider')}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Message informatif selon le mode */}
          {vm.permissionEditMode && (
            <div className={`mb-4 p-3 rounded-md ${
              vm.permissionEditMode === 'add'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
            }`}>
              <p className="text-sm">
                {vm.permissionEditMode === 'add'
                  ? t('collaborator.edit.addModeInfo', 'Sélectionnez les permissions à accorder. Les permissions déjà accordées sont grisées.')
                  : t('collaborator.edit.removeModeInfo', 'Sélectionnez les permissions à supprimer. Les permissions non accordées sont grisées.')}
              </p>
              {vm.tempSelectedPermissionIds.length > 0 && (
                <p className="text-sm font-medium mt-1">
                  {vm.tempSelectedPermissionIds.length} {t('collaborator.edit.selected', 'sélectionné(s)')}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4">
            {Object.entries(vm.groupedPermissions).map(([action, permissions]) => {
              // En mode édition, utiliser les sélections temporaires
              const isEditMode = vm.permissionEditMode !== null;

              // Filtrer les permissions disponibles (non grisées)
              const availablePermissions = permissions.filter(p => !vm.isPermissionDisabled(p.id));

              const allSelected = isEditMode
                ? availablePermissions.every(p => vm.isTempPermissionSelected(p.id))
                : permissions.every(p => vm.isPermissionSelected(p.id));

              const someSelected = isEditMode
                ? availablePermissions.some(p => vm.isTempPermissionSelected(p.id))
                : permissions.some(p => vm.isPermissionSelected(p.id));

              return (
                <div key={action} className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg">
                  <div
                    className={`flex items-center mb-3 ${availablePermissions.length > 0 ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    onClick={() => {
                      if (availablePermissions.length > 0) {
                        isEditMode
                          ? vm.toggleTempPermissionGroup(permissions)
                          : vm.togglePermissionGroup(action, permissions);
                      }
                    }}
                  >
                    <div className={`w-5 h-5 mr-2 rounded flex items-center justify-center border ${
                      allSelected
                        ? 'bg-primary border-primary'
                        : someSelected
                          ? 'bg-primary bg-opacity-30 border-primary'
                          : 'border-gray-400 dark:border-gray-500'
                    }`}>
                      {allSelected && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <h5 className="font-medium text-gray-800 dark:text-gray-200">
                      {action} <span className="text-xs font-normal text-gray-500">({permissions.length})</span>
                    </h5>
                  </div>

                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-7">
                    {permissions.map((permission) => {
                      const isDisabled = vm.isPermissionDisabled(permission.id);
                      const isSelected = isEditMode
                        ? vm.isTempPermissionSelected(permission.id)
                        : vm.isPermissionSelected(permission.id);

                      return (
                        <li key={permission.id} className="flex items-center text-sm">
                          <div
                            className={`w-4 h-4 mr-2 rounded flex items-center justify-center border flex-shrink-0 ${
                              isSelected
                                ? 'bg-primary border-primary'
                                : isDisabled
                                  ? 'bg-gray-300 dark:bg-gray-600 border-gray-400 dark:border-gray-500'
                                  : 'border-gray-400 dark:border-gray-500'
                            } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                            onClick={() => {
                              if (!isDisabled) {
                                isEditMode
                                  ? vm.toggleTempPermission(permission.id)
                                  : vm.togglePermission(permission.id);
                              }
                            }}
                          >
                            {isSelected && !isDisabled && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <span
                            className={`cursor-pointer ${
                              isDisabled
                                ? 'text-gray-400 dark:text-gray-600 line-through'
                                : vm.permissionEditMode === 'remove' && !isDisabled
                                  ? isSelected
                                    ? 'text-white font-bold'
                                    : 'text-white font-medium'
                                  : isSelected
                                    ? 'text-white font-medium'
                                    : 'text-gray-600 dark:text-gray-400'
                            }`}
                            onClick={() => {
                              if (!isDisabled) {
                                isEditMode
                                  ? vm.toggleTempPermission(permission.id)
                                  : vm.togglePermission(permission.id);
                              }
                            }}
                          >
                            {permission.name.split(' ').slice(1).join(' ')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

export default CompanyCollaboratorEditView;
